import {Button, Stack} from '@primer/react'
import styles from './ReviewMenuFooter.module.css'
import {KeybindingHint} from '@primer/react/experimental'
import type {ReviewMode} from './get-allowed-review-mode'
import {FailureBanner} from '../FailureBanner'
import {useRef} from 'react'

type ReviewMenuFooterProps = {
  error?: Error
  handleDismissError: () => void
  handleReviewCancel: () => void
  handleCloseReviewDialog: () => void
  handleReviewSubmit: () => void
  hasPendingReviews: boolean
  submitDisabled?: boolean
  reviewMode: ReviewMode
  reviewSubmissionState: 'idle' | 'submitting' | 'canceling'
}

export function ReviewMenuFooter({
  error,
  handleDismissError,
  handleReviewCancel,
  handleCloseReviewDialog,
  handleReviewSubmit,
  hasPendingReviews,
  submitDisabled,
  reviewMode,
  reviewSubmissionState,
}: ReviewMenuFooterProps) {
  const isCancelling = reviewSubmissionState === 'canceling'
  const isSubmitting = reviewSubmissionState === 'submitting'

  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      {error && (
        <FailureBanner
          error={error}
          title="Failed to submit review"
          onDismiss={handleDismissError}
          returnFocusRef={cancelButtonRef}
          className={styles.ErrorMessage}
        />
      )}
      <div className="d-flex flex-row flex-1 flex-items-center flex-justify-between gap-2">
        {hasPendingReviews ? (
          <Button
            disabled={isCancelling || isSubmitting}
            loading={isCancelling}
            onClick={handleReviewCancel}
            tabIndex={0}
            variant="danger"
          >
            <Stack direction="horizontal" align="center">
              Discard {reviewMode}
            </Stack>
          </Button>
        ) : (
          <div />
        )}
        <div className="d-flex flex-row gap-2">
          <Button onClick={handleCloseReviewDialog} disabled={isCancelling || isSubmitting} ref={cancelButtonRef}>
            Cancel
          </Button>
          <Button
            disabled={submitDisabled || isSubmitting}
            variant="primary"
            onClick={handleReviewSubmit}
            loading={isSubmitting}
            className={styles.SubmitReviewButton}
            trailingVisual={<KeybindingHint keys="Mod+enter" />}
          >
            <div className="d-flex flex-row flex-justify-center">Submit {reviewMode}</div>
          </Button>
        </div>
      </div>
    </>
  )
}
